import z from "zod";

import { SIGNUP_EMAIL_STATUS } from "@/lib/utils/auth/signup-email-status";
import { isSelectableStudentGender } from "@/lib/utils/gender/student-gender";
import { newPasswordZodField } from "./password-field-schema";
import { phoneZodField } from "./phone-field-schema";

export const signupFamilyContactSchema = z.object({
  firstName: z.string().min(1, { message: "First name is required" }),
  lastName: z.string().min(1, { message: "Last name is required" }),
  email: z
    .string()
    .refine((val) => /\S+@\S+\.\S+/.test(val), {
      message: "Please enter a valid email address",
    })
    .refine((val) => val.trim().length <= 50, {
      message: "Email must be at most 50 characters",
    }),
  cellPhone: phoneZodField,
  addressLine1: z.string().min(3, { message: "Address is required" }),
});

export const studentSchema = z.object({
  firstName: z.string().min(1, { message: "First name is required" }),
  lastName: z.string().min(1, { message: "Last name is required" }),
  birthday: z.object({
    day: z.string().min(1, { message: "Day is required" }),
    month: z.string().min(1, { message: "Month is required" }),
    year: z.string().min(1, { message: "Year is required" }),
  }),
  gender: z.string().refine((val) => isSelectableStudentGender(val), {
    message: "Gender is required",
  }),
});

export const signUpSchema = z
  .object({
    email: z.string().refine((val) => /\S+@\S+\.\S+/.test(val), {
      message: "Please enter a valid email address",
    }),
    confirmEmail: z.string().min(1, { message: "Please confirm your email" }),
    password: z.string().optional(),
    confirmPassword: z.string().optional(),
    accountKind: z.enum([
      SIGNUP_EMAIL_STATUS.new,
      SIGNUP_EMAIL_STATUS.existingStudio,
      SIGNUP_EMAIL_STATUS.existingMember,
    ]),
    firstName: z.string().min(1, { message: "First name is required" }),
    lastName: z.string().min(1, { message: "Last name is required" }),
    cellPhone: phoneZodField,
    address: z.string().min(1, { message: "Address is required" }),
    city: z.string().min(1, { message: "City is required" }),
    state: z.string().min(1, { message: "State is required" }),
    zip: z.string().min(1, { message: "Zip code is required" }),
    heardAboutUs: z.string().optional(),
    role: z
      .enum(["selfenroll", "parent"] as const)
      .refine((val) => val !== undefined, {
        message: "Please select your role",
      }),
    locations: z
      .array(z.string())
      .min(1, { message: "Please select at least one location" }),
    familyContacts: z
      .array(signupFamilyContactSchema)
      .min(1, { message: "At least one family contact is required" }),
    students: z.array(studentSchema).optional(),
    terms: z.boolean().refine((val) => val === true, {
      message: "You must accept the Terms and Conditions",
    }),
  })
  .refine((data) => data.email === data.confirmEmail, {
    message: "Emails do not match",
    path: ["confirmEmail"],
  })
  .superRefine((data, ctx) => {
    if (data.accountKind === SIGNUP_EMAIL_STATUS.existingMember) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "An account with this email already exists. Please sign in.",
        path: ["email"],
      });
      return;
    }

    if (data.accountKind === SIGNUP_EMAIL_STATUS.existingStudio) {
      return;
    }

    const passwordResult = newPasswordZodField.safeParse(data.password ?? "");
    if (!passwordResult.success) {
      for (const issue of passwordResult.error.issues) {
        ctx.addIssue({ ...issue, path: ["password"] });
      }
    }

    if (!data.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Please confirm your password",
        path: ["confirmPassword"],
      });
    } else if (data.password !== data.confirmPassword) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Passwords do not match",
        path: ["confirmPassword"],
      });
    }
  });

export type SignUpFormSchema = z.infer<typeof signUpSchema>;
